import React from 'react';
import type {TableColumnsType} from 'antd';
import {Table} from 'antd';
import {ZipcodeType} from "../graphql/queries/getZipcodeListing";
import { formatTimestampToDateTime, timestampToDate } from 'utils/dateTimeUtils';
import { useTranslation } from 'react-i18next';

interface ZipcodeColumnsProps {
    zipcodeData: ZipcodeType[];
    loading: boolean;
    rowSelection?: {
        selectedRowKeys: React.Key[];
        onChange: (selectedKeys: React.Key[]) => void;
    };
}

export const ZipcodeColumns: React.FC<ZipcodeColumnsProps> = ({
    zipcodeData,
    loading,
    rowSelection
}) => {
    const {t} = useTranslation();
    const zipcodeColumns: TableColumnsType<ZipcodeType> = [
        {
            title: t('central.zipcodes.columns.zipcode'),
            dataIndex: 'zipcode',
            key: 'zipcode',
            align: 'left' as const,
            width: 100,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.zipcodes.columns.country'),
            dataIndex: 'country',
            key: 'country',
            align: 'left' as const,
            width: 80,
            ellipsis: false,
            render: () => <span style={{ whiteSpace: 'nowrap' }}>US</span>
        },
        {
            title: t('central.zipcodes.columns.state'),
            dataIndex: 'state',
            key: 'state',
            align: 'left' as const,
            width: 80,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.zipcodes.columns.city'),
            dataIndex: 'city',
            key: 'city',
            align: 'left' as const,
            width: 150,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.zipcodes.columns.timezone'),
            dataIndex: 'timezone',
            key: 'timezone',
            align: 'left' as const,
            width: 120,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.zipcodes.columns.latitude'),
            dataIndex: 'latitude',
            key: 'latitude',
            align: 'left' as const,
            width: 120,
            ellipsis: false,
            render: (value: number) => <span style={{ whiteSpace: 'nowrap' }}>{value ? value.toFixed(3) : '-'}</span>
        },
        {
            title: t('central.zipcodes.columns.longitude'),
            dataIndex: 'longitude',
            key: 'longitude',
            align: 'left' as const,
            width: 120,
            ellipsis: false,
            render: (value: number) => <span style={{ whiteSpace: 'nowrap' }}>{value ? value.toFixed(3) : '-'}</span>
        },
        {
            title: t('central.zipcodes.columns.isDelivery'),
            dataIndex: 'isDelivery',
            key: 'isDelivery',
            align: 'left' as const,
            width: 120,
            ellipsis: false,
            render: (value: boolean) => {
                const text = value === true ? t('enum.yesorNoStatus.yes') : t('enum.yesorNoStatus.no');
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.zipcodes.columns.isPickup'),
            dataIndex: 'isPickup',
            key: 'isPickup',
            align: 'left' as const,
            width: 120,
            ellipsis: false,
            render: (value: boolean) => {
                const text = value === true ? t('enum.yesorNoStatus.yes') : t('enum.yesorNoStatus.no');
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.zipcodes.columns.allowTakingOrder'),
            dataIndex: 'allowTakingOrder',
            key: 'allowTakingOrder',
            align: 'left' as const,
            width: 120,
            ellipsis: false,
            render: (value: boolean) => {
                const text = value === true ? t('enum.yesorNoStatus.yes') : t('enum.yesorNoStatus.no');
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.zipcodes.columns.isTakingDeliveryOrder'),
            dataIndex: 'isTakingDeliveryOrder',
            key: 'isTakingDeliveryOrder',
            align: 'left' as const,
            width: 150,
            ellipsis: false,
            render: (value: boolean) => {
                const text = value === true ? t('enum.yesorNoStatus.yes') : t('enum.yesorNoStatus.no');
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.zipcodes.columns.sortingCode'),
            dataIndex: 'sortingCode',
            key: 'sortingCode',
            align: 'left' as const,
            width: 150,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.zipcodes.columns.updatedTime'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            align: 'left' as const,
            width: 200,
            ellipsis: false,
            render: (text: string) => {
                if (!text) return <span style={{ whiteSpace: 'nowrap' }}>-</span>;
                const formatted = formatTimestampToDateTime(text);
                return <span style={{ whiteSpace: 'nowrap' }}>{formatted}</span>;
            },
        },
    ];

    return (
        <Table
            loading={loading}
            rowKey="zipcode"
            columns={zipcodeColumns}
            dataSource={zipcodeData}
            scroll={{x: 'max-content'}}
            size="middle"
            tableLayout="auto"
            pagination={false}
            className="mb-4"
            rowSelection={rowSelection}
        />
    );
}; 